import { useMemo, useEffect, useRef, useState, useCallback, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DocTypeListPageShell } from "@/features/common/components/DocTypeListPageShell";
import { LIST_PAGE_SHELL } from "@/components/layout/pageShell";
import { useStockEntryListWithPagination } from "./api/stockEntryHooks";
import type { StockEntry } from "@/lib/erp";
import { useFilterStore } from "@/lib/store/filterStore";
import type { FilterDescriptor } from "@/lib/erp";
import { getListViewSchema } from "@/lib/views/listViewSchemas";
import type { ListViewFilter } from "@/lib/views/listViewSchemas/types";
import {
  getActiveListViewConfig,
  buildColumnsFromListViewConfig,
  convertListViewSortToTanStackSort,
  convertListViewFiltersToFilterDescriptors,
} from "@/lib/views/utils/buildListColumnsFromSchema";
import { ListQuickFiltersBar } from "@/components/list/ListQuickFiltersBar";
import { buildNewRoute } from "@/lib/config/docRoutes";
import { injectActionsColumn } from "@/features/list/actionsColumn";
import { GenericInlineDetailPanel } from "@/features/list/GenericInlineDetailPanel";
import { GenericDetailPanel } from "@/features/list/GenericDetailPanel";
import { useDetailPanelState } from "@/features/list/useDetailPanelState";
import { ArrowLeftRight } from "lucide-react";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";

export function StockEntryListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasInitializedFilters = useRef(false);
  const [inlineDetailDocName, setInlineDetailDocName] = useState<string | null>(null);
  const handleInlineClose = useCallback(() => setInlineDetailDocName(null), []);
  const { detailPanelItem, panelItem, openDetailPanel, closeDetailPanel } = useDetailPanelState();

  const renderSubRow = useCallback(
    (row: StockEntry, columnInfo: SubRowColumnInfo): ReactNode | null => {
      if (!row.name || row.name !== inlineDetailDocName) return null;
      return (
        <GenericInlineDetailPanel
          key={row.name}
          doctype="Stock Entry"
          docName={row.name}
          rowData={row as unknown as Record<string, unknown>}
          onClose={handleInlineClose}
          columns={columnInfo}
        />
      );
    },
    [inlineDetailDocName, handleInlineClose],
  );

  // Initialize filters from URL params on mount (one-time only)
  useEffect(() => {
    // Only run once on mount
    if (hasInitializedFilters.current) return;
    
    const initialFilters: FilterDescriptor[] = [];
    
    // Parse URL params
    const itemCode = searchParams.get("item_code");
    const warehouse = searchParams.get("warehouse");
    const stockEntryType = searchParams.get("stock_entry_type");
    const postingDateFrom = searchParams.get("posting_date_from");
    const postingDateTo = searchParams.get("posting_date_to");
    
    if (itemCode) {
      initialFilters.push({
        field: "item_code",
        operator: "equals",
        value: itemCode,
      });
    }
    
    if (warehouse) {
      // Filter by warehouse (could be from_warehouse or to_warehouse)
      // For simplicity, we'll filter by from_warehouse, but this could be enhanced
      initialFilters.push({
        field: "from_warehouse",
        operator: "equals",
        value: warehouse,
      });
    }
    
    if (stockEntryType) {
      initialFilters.push({
        field: "stock_entry_type",
        operator: "equals",
        value: stockEntryType,
      });
    }
    
    if (postingDateFrom || postingDateTo) {
      const dateRange: string[] = [];
      if (postingDateFrom) dateRange.push(postingDateFrom);
      if (postingDateTo) dateRange.push(postingDateTo);
      
      if (dateRange.length > 0) {
        initialFilters.push({
          field: "posting_date",
          operator: dateRange.length === 2 ? "between" : postingDateFrom ? "greaterOrEqual" : "lessOrEqual",
          value: dateRange.length === 2 ? dateRange : dateRange[0],
        });
      }
    }
    
    // Set initial filters in store (only on mount, only for this scope)
    if (initialFilters.length > 0) {
      useFilterStore.getState().setFiltersForKey("stock.stock_entry", initialFilters);
    }
    
    hasInitializedFilters.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount, ref prevents re-runs

  // Get schema and view config
  const schema = getListViewSchema("Stock Entry");
  const viewConfig = getActiveListViewConfig(schema, "experience");

  // Initialize active quick filter from schema default
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(() => {
    const defaultFilter = viewConfig?.quickFilters?.find((f) => f.default);
    return defaultFilter?.id ?? null;
  });

  // Handle quick filter change
  const handleQuickFilterChange = (
    nextId: string | null,
    pillFilters: ListViewFilter[]
  ) => {
    setActiveQuickFilterId(nextId);

    // Compute effective filters: defaultFilters + pill filters
    const defaultFilters = viewConfig?.defaultFilters ?? [];
    const effectiveFilters = [
      ...defaultFilters,
      ...pillFilters,
    ];

    // Convert to FilterDescriptor[] and set in store
    const filterDescriptors = convertListViewFiltersToFilterDescriptors(
      effectiveFilters,
      "Stock Entry"
    );
    
    useFilterStore.getState().setFiltersForKey("stock.stock_entry", filterDescriptors);
  };

  // Build columns from schema
  const buildColumns = useMemo(
    () => {
      if (!viewConfig) {
        return () => [];
      }
      const schemaColumns = buildColumnsFromListViewConfig<StockEntry>(viewConfig);
      return ({
        items: _items,
        visibleFieldOrder,
        availableFields
      }: {
        items: StockEntry[];
        visibleFieldOrder?: string[];
        availableFields?: Array<{ fieldname: string; label: string; fieldtype?: string }>;
      }) => {
        void _items;
        void visibleFieldOrder;
        void availableFields;
        return injectActionsColumn(schemaColumns, {
          onToggleDetail: (name) => setInlineDetailDocName((prev) => prev === name ? null : name),
          onOpen: (name) => openDetailPanel(name),
        });
      };
    },
    [viewConfig]
  );

  // Apply initial quick filter on mount (if default exists) - but only if URL params didn't set any
  useEffect(() => {
    // Don't override URL-based filters
    if (hasInitializedFilters.current) {
      return;
    }

    if (!viewConfig || !viewConfig.quickFilters || activeQuickFilterId === null) {
      return;
    }

    const activeFilter = viewConfig.quickFilters.find(
      (f) => f.id === activeQuickFilterId
    );

    if (activeFilter) {
      const defaultFilters = viewConfig.defaultFilters ?? [];
      const effectiveFilters = [
        ...defaultFilters,
        ...activeFilter.filters,
      ];

      const filterDescriptors = convertListViewFiltersToFilterDescriptors(
        effectiveFilters,
        "Stock Entry"
      );

      // Only set if store is empty (don't override URL filters)
      const currentFilters = useFilterStore.getState().scopedFilters["stock.stock_entry"];
      if (!currentFilters || currentFilters.length === 0) {
        useFilterStore.getState().setFiltersForKey("stock.stock_entry", filterDescriptors);
      }
    }
  }, [viewConfig, activeQuickFilterId]);

  // Get default sort from schema
  const defaultSorting = useMemo(() => {
    if (!viewConfig?.defaultSort) {
      return [{ id: "posting_date", desc: true }];
    }
    return convertListViewSortToTanStackSort(viewConfig.defaultSort);
  }, [viewConfig]);

  return (
    <div className={LIST_PAGE_SHELL}>
      <div>
        <DocTypeListPageShell<StockEntry>
          doctype="Stock Entry"
          columns={buildColumns}
          filterStoreKey="stock.stock_entry"
          useListHook={useStockEntryListWithPagination}
          defaultSorting={defaultSorting}
          pageSizeOptions={viewConfig?.pageSizeOptions ?? [20, 50, 100, 200, 400, 500]}
          searchPlaceholder="Search by name or remark..."
          title="Stock Entries"
          newButtonLabel="New Stock Entry"
          onNewClick={() => navigate(buildNewRoute("Stock Entry"))}
          onRowClick={undefined}
          renderSubRow={renderSubRow}
          activeSubRowId={inlineDetailDocName ?? undefined}
          getRowId={(row) => row.name}
          inlineEditable
          isInlineEditMode
          compactMode
          noPadding
          fullWidth
          className="pt-0"
          tableContainerClassName="overflow-x-auto overflow-y-hidden border-y border-border bg-white dark:bg-neutral-950"
          breadcrumbs={[
            {
              label: "Stock Entries",
              icon: (
                <span className="flex items-center justify-center h-5 w-5 rounded-xs bg-orange-500 shrink-0">
                  <ArrowLeftRight className="h-3 w-3 text-white" />
                </span>
              ),
            },
          ]}
          enableViewModeSwitch={true}
          headerBelow={
            viewConfig?.quickFilters && viewConfig.quickFilters.length > 0 ? (
              <ListQuickFiltersBar
                doctype="Stock Entry"
                quickFilters={viewConfig.quickFilters}
                activeFilterId={activeQuickFilterId}
                onChange={handleQuickFilterChange}
              />
            ) : undefined
          }
          listSettingsConfig={{
            key: "stock.stock_entry",
            meta: {
              doctype: "Stock Entry",
              includeFieldtypes: [
                "Data",
                "Currency",
                "Date",
                "Datetime",
                "Check",
                "Int",
                "Float",
                "Link",
                "Select",
                "Small Text",
                "Text",
                "Long Text",
              ],
              excludeFieldtypes: [
                "Section Break",
                "Column Break",
                "HTML",
                "Table",
                "Table MultiSelect",
                "Button",
                "Heading",
              ],
              excludeFieldnames: [
                "naming_series",
                "owner",
                "creation",
                "modified",
                "modified_by",
                "idx",
                "__islocal",
                "__unsaved",
                "amended_from",
                "amended_date",
                "_assign",
                "_comment_count",
                "_liked_by",
              ],
              respectInListView: true,
              requiredFieldnames: ["name"],
              defaultVisibleFieldnames: viewConfig?.columns?.map((col) => col.fieldname) || [],
            },
          }}
        />
      </div>

      <GenericDetailPanel
        open={!!detailPanelItem}
        onClose={closeDetailPanel}
        doctype="Stock Entry"
        docName={panelItem?.name ?? ""}
        docLabel={panelItem?.label}
      />
    </div>
  );
}

